import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SourceControl } from "@/components/origination/SourceControl";
import { useOrigination } from "@/hooks/useOrigination";
import { useAuth } from "@/lib/auth";
import type { OriginationInput } from "@shared/originationConstants";

const ENGAGEMENT_TYPES = ["M&A", "Capital Raise", "Debt Placement", "Restructuring", "ESOP", "Other"];

/**
 * The one way a prospect becomes a client. Nudges for a source when none is
 * recorded, since the partner who remembers it is in the room right now.
 */
export function ConvertToClientDialog({ open, onOpenChange, company }: {
  open: boolean; onOpenChange: (o: boolean) => void; company: { id: string; name: string; engagementType: string | null };
}) {
  const { user } = useAuth();
  const { data: origination } = useOrigination(company.id);
  const [engagementType, setEngagementType] = useState(company.engagementType ?? "M&A");
  const [dealLead, setDealLead] = useState("");
  const [source, setSource] = useState<OriginationInput | null>(null);
  const [busy, setBusy] = useState(false);

  const convert = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/prospects/convert/${company.id}`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ engagementType, dealLead: dealLead || null, origination: origination ? null : source }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Conversion failed");
      toast.success("Converted to client");
      onOpenChange(false);
      window.location.href = `/clients/${company.id}?promptProject=true`;
    } catch (e: any) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Convert to Client</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">This moves {company.name} from Prospects to the Clients pipeline. Its source travels with it.</p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Engagement type</Label>
            <Select value={engagementType} onValueChange={setEngagementType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ENGAGEMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Deal lead (optional)</Label>
            <Input value={dealLead} onChange={(e) => setDealLead(e.target.value)} placeholder="Team member name" className="h-9" />
          </div>
          {!origination && (
            <div className="rounded-md border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 p-3">
              <p className="text-xs text-muted-foreground mb-2">No source is recorded for this company. Where did it come from?</p>
              <SourceControl value={source} onChange={setSource} currentUserId={user?.id} />
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="gold-gradient text-primary-foreground font-semibold" onClick={convert} disabled={busy}>Convert to Client</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
